import React, { memo } from 'react';
import { Loader } from '@getro/rombo';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass/styled-components';

import { blockBoardPanningAttr } from './dataAttributes';
import { CardList } from '../boardCardList';
import { CardShadow } from '../boardCard/cardShadow';
import { useBoardColumn } from './useBoardColumn';
import { BoardColumnHeader } from '../boardColumnHeader';

export const BoardColumn = memo(
  ({ column, CardComponent, disableVerticalDragging, onNewColumnName, onDeleteColumn }) => {
    const {
      state,
      outerFullHeightRef,
      scrollableRef,
      headerRef,
      innerRef,
      cards,
      cardsCount,
      isLoading,
      isFetching,
      isForceRefetching,
      page,
      onEndReached,
    } = useBoardColumn({ column });

    const stateStyles = {
      idle: {
        border: '2px solid transparent',
      },
      'is-card-over': {
        border: '2px dashed',
        borderColor: 'purple.300',
        bg: 'purple.50',
      },
      'is-column-over': {
        border: '2px dashed',
        borderColor: 'purple.300',
        bg: 'purple.50',
      },
      'is-dragging': {
        opacity: 0.4,
      },
    };

    return (
      <Flex
        sx={{
          width: '360px',
          flexShrink: 0,
          userSelect: 'none',
          flexDirection: 'column',
          ...(isFetching && !isLoading
            ? {
                cursor: 'progress',
                '&& .board-card': { pointerEvents: 'none' },
              }
            : {}),
        }}
        ref={outerFullHeightRef}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            maxHeight: '100%',
            flexDirection: 'column',
            borderRadius: '8px',
            bg: 'slate.800',
            ...(state.type === 'is-column-over' && column.id === 'column:unassigned'
              ? { border: '2px solid transparent' }
              : stateStyles[state.type]),
          }}
          ref={innerRef}
          {...{ [blockBoardPanningAttr]: true }}
        >
          {/* Extra wrapping element to make it easy to toggle visibility of content when a column is dragging over */}
          <Flex
            sx={{
              px: '8px',
              height: '100%',
              flexDirection: 'column',
              visibility: state.type === 'is-column-over' && column.id !== 'column:unassigned' ? 'hidden' : 'visible',
            }}
          >
            <BoardColumnHeader
              headerRef={headerRef}
              cards={cards}
              cardsCount={cardsCount}
              column={{ ...column, cards }}
              hideColumnItemCount={page === 1 && isFetching}
              onNewColumnName={onNewColumnName}
              onDeleteColumn={onDeleteColumn}
            />
            <Flex
              ref={scrollableRef}
              pt="8px"
              flexDirection="column"
              sx={{
                gap: '8px',
                overflowY: 'auto',
                minHeight: 0,
                flex: 1,
                overflowAnchor: 'none',
                scrollbarColor: 'neutral.50 neutral.200',
                scrollbarWidth: 'thin',
              }}
            >
              {isLoading || (page === 1 && isFetching && !isForceRefetching) ? (
                <Flex justifyContent="flex-start" alignItems="flex-start" pt="40px">
                  <Loader size="small" />
                </Flex>
              ) : (
                <CardList
                  column={{ ...column, cards }}
                  CardComponent={CardComponent}
                  disableVerticalDragging={disableVerticalDragging}
                  isFetching={isFetching}
                  onEndReached={onEndReached}
                />
              )}
              {state.type === 'is-card-over' && !state.isOverChildCard ? (
                <Box
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <CardShadow dragging={state.dragging} />
                </Box>
              ) : null}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    );
  },
);

BoardColumn.propTypes = {
  column: PropTypes.object.isRequired,
  CardComponent: PropTypes.func.isRequired,
  onNewColumnName: PropTypes.func.isRequired,
  onDeleteColumn: PropTypes.func.isRequired,
  disableVerticalDragging: PropTypes.bool,
};

BoardColumn.defaultProps = {
  disableVerticalDragging: false,
};
